import { BrowserModule } from '@angular/platform-browser';
import {APP_INITIALIZER, NgModule} from '@angular/core';
import { HttpModule } from '@angular/http';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { AppRoutingModule } from './app-routing.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

/** Core */
import { AppInitService, appInit } from "@core/AppInit.service";
import { LoginComponent } from '@core/auth/login.component';
import { LogoutComponent } from '@core/auth/logout.component';
import { UnauthorizedAccessComponent } from '@core/auth/UnauthorizedAccess.component';
import { TRANSLATION_PROVIDERS, TranslatePipe, TranslateService } from '@core/translate';
import { BroadcastService } from '@core/broadcast/broadcast.service';
import { AuthService } from '@core/auth/auth.service';
import { AuthTokenService } from '@core/auth/auth-token.service';
import { StorageFactory } from '@core/storage/storage.factory';
import { AuthInterceptor } from '@core/auth/auth.interceptor';
import { BroadcasterService } from '@core/broadcaster/broadcaster.service';
import { LandEvent } from '@core/broadcaster/LandEvent';
import { ValidatorsService } from '@core/validators/validators.service';
import {GlobalSearchService} from "@core/globalSearch/globalSearch.service";
import {ThingService} from "@core/thing/thing.service";
import {ParkService} from "@core/park/park.service";

/** Components */
import { HomeButtonComponent } from "@components/home-button/home-button.component";
import { AreasListComponent } from '@components/areas/list/list.component';
import { AreaCardComponent } from '@components/areas/card/card.component';
import { AreasEditComponent } from '@components/areas/edit/edit.component';
import { AreasFormComponent } from '@components/areas/form/form.component';
import { AreasAddComponent } from '@components/areas/add/add.component';

import {EditThresholdComponent} from "@components/things/threshold/editThreshold.component";
import {RestroomOpeningComponent} from "@components/things/restroomOpening/restroomOpening.component";

import {GlobalSearchComponent} from "@components/globalSearch/globalSearch.component";
import {GlobalSearchPanelComponent} from "@components/globalSearch/panel/panel.component";
import {GlobalSearchListComponent} from "@components/globalSearch/list/list.component";

/** Events **/
import { MainKngContentScrollEvent } from "@events/MainKngContentScrollEvent";
import { OpenLandModalEvent } from '@events/OpenLandModalEvent';
import { RefreshEvent } from '@events/RefreshEvent';
import { ThingModalEvent } from '@events/ThingModalEvent';
import { RedrawMapEvent } from "@events/RedrawMapEvent";
import { ThingToggleEvent } from '@events/things/ThingToggleEvent';
import { LatLongEvent } from '@events/LatLongEvent';
import { ThingTypeEvent } from '@events/things/ThingTypeEvent';
import { CannotGetAuthTokenEvent } from "@events/CannotGetAuthTokenEvent";
import { UnauthorizedAPIEvent } from "@events/UnauthorizedAPIEvent";

/** Pipes **/
import { ActiveFilterPipe } from '@core/pipes/activeFilter.pipe';
import { NameFilterPipe } from "@core/pipes/nameFilter.pipe";

import {ThresholdEndTimePipe} from "@components/things/threshold/thresholdEndTime.pipe";
import {AreaFilterPipe} from "@components/areas/list/area-filter.pipe";

/** Layout */
import { KngInlineActivityIndicatorComponent} from "@layout/kingswell/kng-activity-indicator/kng-inline-activity-indicator.component";
import { KngSideMenuComponent } from '@layout/kingswell/kng-side-menu/kng-side-menu.component';
import { KngProfileHeaderButton } from '@layout/kingswell/kng-profile-header-button/kng-profile-header-button.component';
import { KngBubbleTipIconComponent} from '@layout/kingswell/kng-bubble-tip/kng-bubble-tip-icon.component';
import { KngBubbleTipComponent, KngBubbleTipWindow} from '@layout/kingswell/kng-bubble-tip/kng-bubble-tip.component';
import { KngCheckboxComponent } from "@layout/kingswell/kng-checkbox/kng-checkbox.component";
import { KngContent } from '@layout/kingswell/kng-content/kng-content.component';
import { KngExpandCollapseComponent } from '@layout/kingswell/kng-expand-collapse/kng-expand-collapse.component';
import { KngCardsComponent } from '@layout/kingswell/kng-cards/kng-cards.component';
import { KngFooterComponent } from "@layout/kingswell/kng-footer/kng-footer.component";
import { KngHeaderComponent } from "@layout/kingswell/kng-header/kng-header.component";
import { KngIconButtonComponent } from "@layout/kingswell/kng-icon-button/kng-icon-button.component";
import { KngPanel, KngProfile } from '@layout/kingswell/kingswell';
import { KngSearchInputComponent } from '@layout/kingswell/kng-search-input/kng-search-input.component';
import { KngModalComponent } from "@layout/kingswell/kng-modal/kng-modal.component";

/** services */
import {KngBreadcrumbsService} from "@layout/kingswell/services/kngBreadcrumbs.service";
import { KngDrawerService} from "@layout/kingswell/services/kngDrawer.service";
import { KngModalService } from "@layout/kingswell/services/kngModal.service";

/** Pages */
import { AreasPageComponent } from '@pages/areas/areas.component';
import { SearchPageComponent} from "@pages/search/search.component";

/** Providers */
import { ParkProvider } from '@providers/park.provider';
import { FileProvider } from '@providers/file.provider';

import { NotificationComponent } from '@components/notification/notification.component';
import { NotificationService } from '@core/notification/notification.service';
import { ThingsPageComponent } from '@pages/things/things.component';
import { ThingsListComponent } from '@components/things/list/list.component';
import { ThingsCardComponent } from '@components/things/card/card.component';
import { ThingProvider } from '@providers/thing.provider';
import { ThingsDetailsComponent } from '@components/things/details/details.component';
import { ThingsMapComponent } from '@components/things/map/map.component';

import { ModalService } from '@core/modal/modal.service';
import { ThingsToggleComponent } from "@components/things/toggle/toggle.component";
import { ThingsAddComponent } from '@components/things/add/add.component';
import { ThingsFormComponent } from '@components/things/form/form.component';
import { ThingsEditComponent } from '@components/things/edit/edit.component';
import { StatusLabelComponent } from '@components/common/statusLabel/statusLabel.component';
import { KngSwitchComponent } from '@layout/kingswell/kng-switch/kng-switch.component';
import { ThingsStatusLabelComponent } from '@components/things/statusLabel/statusLabel.component';
import { ThingsThresholdComponent } from '@components/things/threshold/threshold.component';
import { ThingsUtil } from './util/things';
import { ThresholdUtil } from './util/threshold';
import { AuthGuard } from '@core/auth/auth-guard.service';
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import { ThingsAttributesComponent } from '@components/things/attributes/attributes.component';
import { EditThingsAttributesComponent } from '@components/things/attributes/edit.component';

import { ThingsDetailsPageComponent } from '@pages/things/details.component';
import { KngActivityIndicatorComponent } from '@layout/kingswell/kng-activity-indicator/kng-activity-indicator.component';
import { KngActivityIndicatorService } from '@layout/kingswell/services/kngActivityIndicator.service';
import { formatThingTypePipe } from '@components/things/details/formatType.pipe';
import { formatThingAttributePipe } from '@components/things/attributes/formatAttribute.pipe';
import { FormatThingThresholdPipe } from '@components/things/threshold/formatThingThreshold.pipe';
import { PadThresholdPipe } from '@components/things/threshold/padThreshold.pipe';
import { ResourcesListComponent } from '@components/resources/list/list.component';
import { ResourcesPageComponent } from '@pages/resources/resources.component';
import { ParkUtil } from './util/park';
import { ResourcesFilterComponent } from '@components/resources/filter/filter.component';
import { ResourcesFilterPipe } from '@components/resources/list/resources-filter.pipe';
import { AreasImageSelector } from '@components/areas/form/imageSelector.component';

/** WDPR-RA */
//import { LoggerModule } from '@wdpr/ra-angular-logger';

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    HomeButtonComponent,
    LoginComponent,
    LogoutComponent,
    UnauthorizedAccessComponent,
    KngBubbleTipComponent,
    KngBubbleTipIconComponent,
    KngBubbleTipWindow,
    KngCardsComponent,
    KngCheckboxComponent,
    KngExpandCollapseComponent,
    KngIconButtonComponent,
    KngSideMenuComponent,
    KngProfileHeaderButton,
    KngContent,
    KngFooterComponent,
    KngHeaderComponent,
    KngModalComponent,
    KngPanel,
    KngProfile,
    KngSwitchComponent,
    KngSearchInputComponent,
    AreaFilterPipe,
    TranslatePipe,
    ThresholdEndTimePipe,
    AreaCardComponent,
    AreasListComponent,
    AreasEditComponent,
    AreasFormComponent,
    AreasAddComponent,
    AreasPageComponent,
    NotificationComponent,
    ThingsPageComponent,
    ThingsListComponent,
    ThingsCardComponent,
    ThingsDetailsComponent,
    ThingsMapComponent,
    ThingsToggleComponent,
    ThingsAddComponent,
    ThingsEditComponent,
    ThingsFormComponent,
    EditThresholdComponent,
    StatusLabelComponent,
    StatusLabelComponent,
    ThingsStatusLabelComponent,
    ThingsThresholdComponent,
    RestroomOpeningComponent,
    SearchPageComponent,
    GlobalSearchComponent,
    GlobalSearchPanelComponent,
    GlobalSearchListComponent,
    ThingsAttributesComponent,
    EditThingsAttributesComponent,
    ThingsDetailsPageComponent,
    KngActivityIndicatorComponent,
    KngInlineActivityIndicatorComponent,
    formatThingAttributePipe,
    formatThingTypePipe,
    FormatThingThresholdPipe,
    PadThresholdPipe,
    ResourcesListComponent,
    ResourcesPageComponent,
    ResourcesFilterComponent,
    KngCheckboxComponent,
    ResourcesFilterPipe,
    ActiveFilterPipe,
    NameFilterPipe,
    AreasImageSelector
  ],
  imports: [
    NgbModule.forRoot(),
    AppRoutingModule,
    HttpModule,
    HttpClientModule,
    //LoggerModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule
  ],
  providers: [
    AppInitService,
    TranslateService,
    TRANSLATION_PROVIDERS,
    BroadcastService,
    AuthTokenService,
    AuthService,
    StorageFactory,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    ParkProvider,
    FileProvider,
    ParkService,
    ThingService,
    GlobalSearchService,
    BroadcasterService,
    LandEvent,
    OpenLandModalEvent,
    NotificationService,
    ValidatorsService,
    RefreshEvent,
    ThingProvider,
    ThingModalEvent,
    RedrawMapEvent,
    ModalService,
    ThingToggleEvent,
    LatLongEvent,
    KngBreadcrumbsService,
    KngDrawerService,
    KngModalService,
    ThingsUtil,
    ThresholdUtil,
    AuthGuard,
    KngActivityIndicatorService,
    ThingTypeEvent,
    CannotGetAuthTokenEvent,
    UnauthorizedAPIEvent,
    ParkUtil,
    MainKngContentScrollEvent,
    {
      provide: APP_INITIALIZER,
      useFactory: appInit,
      multi: true,
      deps: [AppInitService]
    }
  ],
  entryComponents: [
    NotificationComponent,
    EditThresholdComponent,
    RestroomOpeningComponent,
    EditThingsAttributesComponent,
    KngBubbleTipWindow,
    KngModalComponent,
    ThingsAddComponent,
    ThingsFormComponent,
    ResourcesFilterComponent,
    ThingsToggleComponent,
    AreasImageSelector
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }



// WEBPACK FOOTER //
// D:/dclean-web-latest/cot-web/src/app/app.module.ts